import { useCallback, useState } from "react";

// Reusable guided-tour state, backed by localStorage. One hook powers
// every page's tour — pass a unique `tourId` per tour ("login",
// "onboarding", "dashboard", "create-child", ...) and it gets its own
// completion flag, independent of every other tour.
//
// This hook only tracks state (active/completed); *when* a tour should
// show is a policy decision left entirely to the caller. Every page uses
// the one starter, start() — unconditionally show it now, unless already
// completed — typically called on mount, so the tour shows on this
// browser's/account's first-ever visit to that page and never again.
//
// (Dashboard.jsx used to gate its own tour behind a separate "did
// Register.jsx just queue this" pending flag instead of calling start()
// directly — removed 2026-08-19: it meant a normal sign-in, or Register's
// own queue getting lost across the Register -> Onboarding -> results-
// screen -> Dashboard hand-off, never showed the tour at all. Confirmed
// live: signing in and landing on the dashboard for the first time did
// not guide the guardian through anything.)
function completedKey(tourId) {
    return `poems_${tourId}_completed`;
}

function readCompleted(tourId) {
    try {
        return localStorage.getItem(completedKey(tourId)) === "true";
    } catch {
        // localStorage unavailable (private browsing, disabled, ...) —
        // default to "not completed" so the tour still shows rather than
        // silently never appearing.
        return false;
    }
}

export default function useTour(tourId) {
    const [active, setActive] = useState(false);

    const start = useCallback(() => {
        if (!readCompleted(tourId)) setActive(true);
    }, [tourId]);

    const finish = useCallback(() => {
        try {
            localStorage.setItem(completedKey(tourId), "true");
        } catch { /* ignore */ }
        setActive(false);
    }, [tourId]);

    // Testing helper: wipe the stored completion flag and restart from
    // step one, bypassing whatever gated this tour originally.
    const replay = useCallback(() => {
        try {
            localStorage.removeItem(completedKey(tourId));
        } catch { /* ignore */ }
        setActive(true);
    }, [tourId]);

    return { active, start, finish, replay };
}
